/***
 *
 *   PublicIntakeStart (feature 008)
 *   The public self-serve landing page a firm's client reaches via the stable
 *   /public/intake/:token link. Collects email + name, runs reCAPTCHA v3 and a
 *   honeypot, then POSTs to start a Tranche 2 verification and hands the client
 *   into the existing /public/tranche2/verification/:id flow.
 *
 *   The fields render as a SurveyJS survey rather than hand-rolled inputs. The
 *   very next screen this visitor sees is the SurveyJS consent form, and the
 *   endpoint already returns that journey's theme as `brand_theme` — so running a
 *   small hardcoded schema through the same applyFormTheme() the consent step uses
 *   is what makes the two screens read as one product. It also puts the primary
 *   button on --sjs-primary-backcolor, which the app's Button cannot do: its colour
 *   is a compiled Tailwind class and it forwards no style prop.
 *
 **********/

import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import { Card, Loader, Message, useAPI } from "components/lib";
import { applyFormTheme, useFormBrandColor } from "components/survey/formTheme";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";
import logo from "../../assets/IdentityCheck_Logo_RGB_Purple.png";
const Settings = require("settings.json");

StylesManager.applyTheme("defaultV2");

// Prefer the client-configured v3 site key (same source as signup); fall back to
// the value the API returns (which is null unless GOOGLE_RECAPTCHA_SITE_KEY is set).
const configuredSiteKey =
  Settings?.[process.env.NODE_ENV]?.recaptcha?.site_key || null;

/*
 * The landing fields, hand-authored rather than fetched: this is not one of the
 * firm's configurable Tranche 2 modules, and the start endpoint validates exactly
 * these three keys. Question names therefore ARE the POST body keys — renaming one
 * silently breaks utility.validate() server-side, which is what the schema-contract
 * test guards against.
 */
export const PUBLIC_INTAKE_SCHEMA = {
  showQuestionNumbers: "off",
  completeText: "Start verification",
  pages: [
    {
      name: "details",
      elements: [
        {
          type: "text",
          name: "first_name",
          title: "First name",
          isRequired: true,
          maxLength: 255,
          autocomplete: "given-name",
        },
        {
          type: "text",
          name: "last_name",
          title: "Last name",
          isRequired: true,
          maxLength: 255,
          autocomplete: "family-name",
        },
        {
          type: "text",
          name: "email",
          title: "Email address",
          isRequired: true,
          inputType: "email",
          maxLength: 255,
          autocomplete: "email",
          // inputType already validates the address in survey-core 1.11.8; the
          // validator is here only to replace its default "valid e-mail address"
          // wording, which is the one string a visitor reads when they mistype.
          validators: [
            { type: "email", text: "Please enter a valid email address." },
          ],
        },
      ],
    },
  ],
};

function loadRecaptcha(siteKey) {
  if (!siteKey || typeof document === "undefined") return;
  if (document.querySelector(`script[data-recaptcha="${siteKey}"]`)) return;
  const script = document.createElement("script");
  script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
  script.async = true;
  script.setAttribute("data-recaptcha", siteKey);
  document.body.appendChild(script);
}

// grecaptcha can hang outright — a blocked script, an offline tab — and completing
// the survey swaps the whole card for a loader, so an unsettled promise would
// strand the visitor there indefinitely. Resolving empty instead lets the server
// refuse with a message the visitor can act on.
const RECAPTCHA_TIMEOUT_MS = 10000;

function getRecaptchaToken(siteKey) {
  return new Promise((resolve) => {
    if (!siteKey || !window.grecaptcha) return resolve("");

    const timer = setTimeout(() => resolve(""), RECAPTCHA_TIMEOUT_MS);
    const settle = (token) => {
      clearTimeout(timer);
      resolve(token || "");
    };

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey, { action: "public_intake" })
        .then(settle)
        .catch(() => settle(""));
    });
  });
}

/* Same shape as tranche2-page.js, so both public surfaces theme identically. */
function buildSurveyModel(schema, theme) {
  if (!schema || typeof schema !== "object") return null;
  const model = new Model(schema);
  model.showCompletedPage = false;
  return applyFormTheme(model, theme);
}

/*
 * The landing model: the shared shape above, plus trimming.
 *
 * The old hand-rolled <input type="email"> silently stripped surrounding
 * whitespace per the HTML value-sanitization algorithm; SurveyJS does not, so a
 * pasted "ada@example.com " would fail the email validator. Values commit on blur
 * (textUpdateMode defaults to "onBlur"), so trimming on change cannot fight
 * typing — "Mary Jane" keeps its space.
 */
export function buildIntakeSurvey(theme) {
  const model = buildSurveyModel(PUBLIC_INTAKE_SCHEMA, theme);
  if (!model) return model;

  model.onValueChanging.add((_sender, options) => {
    if (typeof options.value === "string") options.value = options.value.trim();
  });

  return model;
}

/* Drops the completed state but keeps what was typed, so a failed submit leaves a
   usable form rather than an empty card. */
function restoreSurveyForRetry(model) {
  if (!model || typeof model.clear !== "function") return;
  model.clear(false, false);
}

/*
 * Picked explicitly rather than spread: the server validates these three keys at
 * the top level, `sender.data` is untrimmed, and a stray space would reach the
 * disposable-domain and MX checks as part of the domain.
 */
export function buildStartPayload(surveyData, honeypot, recaptchaToken) {
  const values = surveyData || {};
  const text = (value) => String(value == null ? "" : value).trim();

  return {
    email: text(values.email),
    first_name: text(values.first_name),
    last_name: text(values.last_name),
    recaptchaToken: recaptchaToken || "",
    confirm_password: honeypot || "",
  };
}

export function PublicIntakeStart() {
  const { token } = useParams();
  const lookup = useAPI(`/api/public/intake/${token}`);

  const data = lookup?.data;

  // the landing page opens the journey, so it takes the journey colour (R8)
  useFormBrandColor(data?.brand_theme, !!data);
  const siteKey = configuredSiteKey || data?.recaptcha_site_key || null;

  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // useAPI sets its state once, so `brand_theme` is referentially stable after the
  // fetch and the model is built at most twice.
  const survey = useMemo(
    () => buildIntakeSurvey(data?.brand_theme),
    [data?.brand_theme]
  );

  useEffect(() => {
    if (siteKey) loadRecaptcha(siteKey);
  }, [siteKey]);

  // useAPI starts at { data: null, loading: false }, so without the !data guard the
  // very first render falls through with no payload and paints the "link is
  // unavailable" card for a frame before the real one arrives. `submitting` belongs
  // here too: completing the survey immediately unmounts it (showCompletedPage is
  // false), so without it the visitor watches an empty card for the whole reCAPTCHA
  // round trip and POST.
  if (lookup.loading || !data || submitting) return <Loader />;

  const open = data?.open === true;

  const heading = data?.account_name
    ? `Verify your identity with ${data.account_name}`
    : "Verify your identity";

  const handleComplete = async (sender) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const recaptchaToken = await getRecaptchaToken(siteKey);
      const res = await Axios.post(
        `/api/public/intake/${token}/start`,
        buildStartPayload(sender?.data, honeypot, recaptchaToken)
      );

      const result = res.data?.data;
      if (result?.redirect_url) {
        // replace(), not assignment: `submitting` stays true through the navigation,
        // so a history entry here would restore from bfcache into a stuck loader.
        window.location.replace(result.redirect_url);
        return;
      }

      // A closed link comes back 200, so axios does not throw — this branch has to
      // restore the form too, or the warning renders above an empty card.
      restoreSurveyForRetry(sender);
      setSubmitError(
        "This verification link is currently unavailable. Please contact the firm that sent it."
      );
      setSubmitting(false);
    } catch (err) {
      // Restore before releasing the guard: these run after an await, so React 17
      // does not batch them and the order is observable.
      restoreSurveyForRetry(sender);
      setSubmitError(
        err.response?.data?.message ||
          "We couldn't start your verification. Please check your details and try again."
      );
      setSubmitting(false);
    }
  };

  // SurveyJS swallows implicit form submission: its <form> preventDefaults every
  // submit and the complete control is an <input type="button">. On a three-field
  // form Enter is the natural action, and the page it replaced honoured it. Blur
  // first — values commit on blur, so completing without it would validate the
  // value as it was before the last keystroke.
  const handleKeyDown = (event) => {
    if (!survey || event.key !== "Enter" || event.shiftKey) return;
    if (event.target?.tagName !== "INPUT") return;

    event.preventDefault();
    if (document.activeElement?.blur) document.activeElement.blur();
    survey.completeLastPage();
  };

  if (survey) {
    // Re-registered every render so the handler closes over the current honeypot
    // and siteKey while the model itself stays memoised; clear() first keeps that
    // idempotent. Same pattern as tranche2-page.js.
    survey.onComplete.clear();
    survey.onComplete.add(handleComplete);
  }

  return (
    <Fragment>
      <div className="w-full max-w-2xl px-4 mx-auto">
        <Card className="w-full mx-auto">
          <div className="flex items-center justify-center mb-6">
            <img src={data?.logo || logo} width="320px" alt="IdentityCheck" />
          </div>

          {/* Mirrors Card's own header rule. Card's `title` prop cannot be used
              directly because it renders above every child, which would put the
              firm's logo underneath the heading. */}
          <div className="relative pb-5 mb-5 border-dotted border-slate-100 border-b">
            <h1 className="m-0 text-lg font-semibold">
              {open ? heading : "This link is unavailable"}
            </h1>
          </div>

          {!open && (
            <p>
              This verification link isn't currently active. Please contact the
              firm that sent it to you for a new link.
            </p>
          )}

          {open && (
            <Fragment>
              {data?.intro && <p className="mb-4">{data.intro}</p>}

              <div className="w-full overflow-x-auto" onKeyDown={handleKeyDown}>
                {submitError && <Message type="warning">{submitError}</Message>}
                {survey && <Survey model={survey} />}
              </div>

              <p className="text-center text-xs text-slate-500 mt-4">
                Protected by reCAPTCHA. This service is supplied by IdentityCheck.
              </p>
            </Fragment>
          )}
        </Card>
      </div>

      {/* Honeypot. It stays a raw DOM input outside <Survey/> on purpose: SurveyJS
          does not render an invisible question to the DOM at all, so a hidden
          `confirm_password` question would be unfillable and could never trip.
          Do not "tidy" this into the schema. */}
      {open && (
        <input
          type="text"
          name="confirm_password"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex="-1"
          autoComplete="off"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            opacity: 0,
          }}
        />
      )}
    </Fragment>
  );
}
